import React, { useEffect, useRef, useState } from "react";
import Network from "../../../utils/Network";
import { GuildType } from "../types";
import TimeAgo from "javascript-time-ago";
import Button from "../../../components/Button";
import { Form } from "react-bootstrap";
import { GuildJoinValues, GuildLanguages } from "../data";
import { useSearchParams } from "react-router-dom";
import { useDebounce } from "use-debounce";
import { formatNumber } from "../../../utils/format-number";
import ReactPaginate from "react-paginate";
import CircularProgress from "../../../components/CircularProgress";
import GuildCrest from "../components/GuildCrest";
import { DEFAULT_GUILD_HALL, guildAsset } from "../utils";
import { useAppTranslation } from "../../../i18n/useAppTranslation";

type Props = {
  /** A guild's hall to show behind the page while its card is pointed at; null puts the page's own back. */
  onTryHall?: (banner: string | null) => void;
  /** False for a player who is already in a guild: the server will not let them found another. */
  canBuild?: boolean;
};

const Explore = ({ onTryHall, canBuild = true }: Props) => {
  const axios = new Network();
  const [guilds, setGuilds] = useState<GuildType[]>();
  const { t, currentLanguage } = useAppTranslation(["common", "guild"]);
  const timeAgo = new TimeAgo(currentLanguage);
  const [paginations, setPaginations] = useState({ total: 1, page: 1 });
  const [searchParameters, setSearchParameters] = useSearchParams();
  const joinType = searchParameters.get("joinType");
  const language = searchParameters.get("language");
  const minScore = searchParameters.get("minScore");
  const hasFreeCapacity = searchParameters.get("hasFreeCapacity");
  const hallTimer = useRef<number>();

  const [debouncedValue] = useDebounce(searchParameters, 1000);
  const getJoinTypeLabel = (value: string | number) =>
    t(`guild:explore.joinTypes.${value}`, {
      defaultValue: GuildJoinValues[value],
    });
  const getLanguageLabel = (value: string) =>
    t(`guild:explore.languages.${value}`, {
      defaultValue: value,
    });

  const handleGuilds = async (page = 1) => {
    const response = await axios.CallApi({
      url: `/guild?page=${page}&count=15&` + searchParameters.toString(),
      method: "get",
    });
    setGuilds(response.data?.data || []);
    setPaginations({ total: Math.ceil(response.data?.totalCount / 15) || 1, page });
  };
  useEffect(() => {
    handleGuilds();
  }, [debouncedValue]);

  useEffect(() => () => window.clearTimeout(hallTimer.current), []);

  const changeQuery = (val: number | string | boolean, name: string) => {
    val ? searchParameters.set(name, val + "") : searchParameters.delete(name);
    setSearchParameters(searchParameters);
  };

  const openGuild = (guild: GuildType) => {
    searchParameters.set("tab", "guild");
    searchParameters.set("id", guild.id + "");
    setSearchParameters(searchParameters);
  };

  // A short wait, so a pointer crossing the list does not flip through every hall on its way.
  const tryHall = (banner: string | null) => {
    window.clearTimeout(hallTimer.current);
    hallTimer.current = window.setTimeout(() => onTryHall?.(banner), banner ? 220 : 80);
  };

  return (
    <div className="gh-explore">
      {canBuild && (
        <aside className="gh-found">
          <img className="gh-found-art" src="/assets/images/castle.png" alt="" />
          <div>
            <h3>{t("guild:explore.cta.title")}</h3>
            <p>
              {t("guild:explore.cta.pitch")}{" "}
              <b>{t("guild:explore.cta.requirement")}</b>
            </p>
          </div>
          <Button
            variant="wide-success"
            onClick={() => {
              changeQuery("create", "tab");
            }}
          >
            {t("guild:explore.cta.action")}
          </Button>
        </aside>
      )}
      <div className="gh-filters gh-panel">
        <label className="gh-filter">
          <img src="/assets/images/icons/letter.png" alt="" />
          {t("guild:explore.filters.type")}
          <Form.Select
            size="sm"
            value={+joinType! || ""}
            onChange={(e: any) => {
              changeQuery(e.target.value, "joinType");
            }}
          >
            <option value="">{t("guild:explore.filters.all")}</option>
            {Object.entries(GuildJoinValues).map(([key, value]) => (
              <option key={key} value={key}>
                {getJoinTypeLabel(key)}
              </option>
            ))}
          </Form.Select>
        </label>
        <label className="gh-filter">
          <img src="/assets/images/icons/earth.png" alt="" />
          {t("guild:explore.filters.language")}
          <Form.Select
            size="sm"
            value={language || ""}
            onChange={(e: any) => changeQuery(e.target.value, "language")}
          >
            <option value="">{t("guild:explore.filters.all")}</option>
            {GuildLanguages.map((lang) => (
              <option key={lang} value={lang}>
                {getLanguageLabel(lang)}
              </option>
            ))}
          </Form.Select>
        </label>
        <label className="gh-filter">
          <img src="/assets/images/icons/crown-flag.png" alt="" />
          {t("guild:explore.filters.joinScore")}
          <Form.Control
            size="sm"
            inputMode="numeric"
            value={minScore || ""}
            placeholder={t("guild:explore.filters.joinScorePlaceholder")}
            onChange={(e: any) => changeQuery(e.target.value, "minScore")}
          />
        </label>
        <Form.Check
          id="gh-show-full"
          checked={!hasFreeCapacity}
          onChange={(e) => changeQuery(!e.target.checked, "hasFreeCapacity")}
          className="fs-med dark-yellow mb-0"
          type="checkbox"
          label={t("guild:explore.filters.showFullCapacityGuilds")}
        />
      </div>

      <div>
        {!guilds ? (
          <div className="gh-status">
            <CircularProgress />
          </div>
        ) : guilds.length === 0 ? (
          <div className="gh-status gh-panel">
            {t("guild:explore.empty.title")}
            <span className="fs-med dark-yellow">{t("guild:explore.empty.hint")}</span>
          </div>
        ) : (
          <div className="gh-cards">
            {guilds.map((guild) => {
              const isFull = guild.playersCount >= guild.capacity;
              return (
                <article
                  key={guild.id}
                  className="gh-card"
                  onClick={() => openGuild(guild)}
                  onMouseEnter={() => tryHall(guild.banner || null)}
                  onMouseLeave={() => tryHall(null)}
                >
                  <div
                    className="gh-card-hall"
                    style={{ backgroundImage: `url("${guildAsset(guild.banner, DEFAULT_GUILD_HALL)}")` }}
                  />
                  <div className="gh-card-head">
                    <GuildCrest image={guild.image} size="small" />
                    <div style={{ minWidth: 0 }}>
                      <h3 className="gh-card-name">{guild.name}</h3>
                      <div className="gh-card-age">
                        {t("guild:explore.row.created", {
                          time: timeAgo.format(new Date(guild.registerDate)),
                        })}
                      </div>
                    </div>
                  </div>
                  <ul className="gh-card-facts">
                    <li>
                      <img src="/assets/images/icons/crown.png" alt="" />
                      {t("guild:explore.row.worth")} <b>{formatNumber(guild.score)}</b>
                    </li>
                    <li>
                      <img src="/assets/images/icons/letter.png" alt="" />
                      <b>{getJoinTypeLabel(guild.joinType)}</b>
                    </li>
                    <li>
                      <img src="/assets/images/icons/earth.png" alt="" />
                      <b>{getLanguageLabel(guild.language)}</b>
                    </li>
                    <li>
                      <img src="/assets/images/icons/crown-flag.png" alt="" />
                      {t("guild:explore.row.joinScore")} <b>{formatNumber(guild.minRankScore)}</b>
                    </li>
                  </ul>
                  <div className="gh-card-foot">
                    <div className="gh-card-room">
                      {isFull ? t("guild:explore.row.full") : t("guild:explore.row.members")} {guild.playersCount} / {guild.capacity}
                      <div className={isFull ? "gh-meter is-full" : "gh-meter"}>
                        <span
                          style={{
                            width: `${Math.min(100, (guild.playersCount / Math.max(1, guild.capacity)) * 100)}%`,
                          }}
                        />
                      </div>
                    </div>
                    <Button
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        openGuild(guild);
                      }}
                      variant="primary"
                    >
                      {t("common:actions.open")}
                    </Button>
                  </div>
                </article>
              );
            })}
          </div>
        )}
        {paginations.total > 1 && guilds && (
          <ReactPaginate
            className="pagination justify-content-center mt-3"
            activeClassName="active-pg"
            pageClassName="page-item"
            pageLinkClassName="page-link"
            breakClassName="page-item"
            nextClassName="page-item"
            nextLinkClassName="page-link"
            breakLinkClassName="page-link"
            previousLinkClassName="page-link"
            previousClassName="page-item"
            previousLabel="<"
            nextLabel=">"
            breakLabel="..."
            onPageChange={(e) => handleGuilds(e.selected + 1)}
            pageRangeDisplayed={3}
            pageCount={paginations.total}
          />
        )}
      </div>

    </div>
  );
};

export default Explore;
